import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';
import useGetMetadataAvatarTypes from '@/hooks/useGetMetadataAvatarTypes';
import {
    AVATAR_RESOURCE_SUPPORTED,
    AVATARS,
    CLIENT_METADATA_AVATAR_TYPE_NAME,
    CLIENTS,
    DEFAULT_CLIENTS_GROUP_AVATAR_ALPHA_ID,
    FINGER_ID,
    FINGERS,
    INCIDENT_SERVICE_CLIENT,
    MEDIA_IMAGE_TYPE,
    MEDIAS,
    TICKETING_LOGO_MEDIA_TAG_NAME,
} from '@/utils/CONST';
import { getFileBase64 } from '@/utils/files';
import extractIdFromURI from '@/utils/id';
import { Avatar } from '@/utils/types';
import useGetAlphaId from '@/utils/useGetAlphaId';
import { PersonAddAltOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { composeUriId } from 'base-tools-360smartconnect/utils/compose';
import { Form, ImageField, ImageInput, required, TextInput, useCreate, useNotify, useTranslate } from 'react-admin';

interface CreateClientDialogProps {
    open: boolean;
    onClose: () => void;
}

const CreateClientDialog = ({ open, onClose }: CreateClientDialogProps) => {
    const translate = useTranslate();
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const notify = useNotify();
    const { isLoadingGetAlphaId, getAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_CLIENT,
    });
    const { currentUser } = useGetCurrentUser();
    const { firstMetadataAvatarTypeId, isLoading: isLoadingMetadataAvatarType } = useGetMetadataAvatarTypes({
        filters: {
            alphaId: CLIENT_METADATA_AVATAR_TYPE_NAME,
        },
    });
    const { firstGroupAvatarId: defaultClientsGroupAvatarId, isLoading: isLoadingDefaultClientsGroup } =
        useGetGroupAvatars({
            filters: {
                alphaId: DEFAULT_CLIENTS_GROUP_AVATAR_ALPHA_ID,
            },
        });

    const handleSubmit = ({ media, name }: any) => {
        getAlphaId().then((data: { counter: number; identifier: string }) => {
            create(
                CLIENTS,
                {
                    data: {
                        name: name,
                        alphaId: `Ct:${data.counter}`,
                        mcs: [],
                        generateMCFinger: composeUriId(FINGERS, FINGER_ID),
                        generateMCQuantity: 1,
                        metadataAvatarType: firstMetadataAvatarTypeId,
                        company: currentUser?.company,
                        groupAvatars: [defaultClientsGroupAvatarId],
                    },
                    meta: {
                        api: 'v2',
                        realResource: AVATARS,
                    },
                },
                {
                    onSuccess: async (avatar: Avatar) => {
                        if (media) {
                            const fileData: any = await getFileBase64(media.rawFile);

                            let finalMedia = {
                                fileName: media.title,
                                base64data: fileData,
                                mediaType: MEDIA_IMAGE_TYPE,
                                resourceSupported: AVATAR_RESOURCE_SUPPORTED,
                                resourceId: extractIdFromURI(avatar?.id),
                                tags: [TICKETING_LOGO_MEDIA_TAG_NAME],
                                customFields: {
                                    tags: [TICKETING_LOGO_MEDIA_TAG_NAME],
                                },
                            };

                            await create(
                                MEDIAS,
                                {
                                    data: finalMedia as any,
                                },
                                {
                                    returnPromise: true,
                                },
                            );
                        }

                        notify('ra.notification.created', { type: 'success' });
                        onClose();
                    },
                    onError: () => {
                        notify('messages.client.fail_creation', { type: 'error' });
                    },
                },
            );
        });
    };

    const isLoading =
        isLoadingCreate || isLoadingGetAlphaId || isLoadingMetadataAvatarType || isLoadingDefaultClientsGroup;

    return (
        <DialogTemplate
            open={open}
            onClose={onClose}
            icon={<PersonAddAltOutlined />}
            title={translate('client.create_client_label')}
            closeButtonDisabled={isLoading}
        >
            <Form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            helperText={false}
                            source={'name'}
                            label={'client.form.client_name'}
                            validate={required()}
                            autoFocus
                        />
                    </FormInputsSeparator>

                    <ImageInput
                        source={'media'}
                        label={'buttons.add_logo'}
                        maxSize={30000000}
                        helperText={false}
                        sx={{
                            '& .RaLabeled-label': {
                                fontSize: 16,
                                color: 'black',
                            },
                            '& .RaFileInput-dropZone': {
                                display: 'grid',
                                alignItems: 'center',
                                border: (theme: any) => `1px solid ${theme.palette.action.disabled}`,
                            },
                            '& .previews': {
                                display: 'flex',
                                justifyContent: 'center',
                            },
                        }}
                    >
                        <ImageField source='src' title='title' />
                    </ImageInput>

                    <ButtonsBlock>
                        <Button onClick={onClose} disabled={isLoading}>
                            {translate('ra.action.back')}
                        </Button>

                        <LoadingButton loading={isLoading} type={'submit'} variant={'contained'}>
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateClientDialog;
